import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePermissions } from "../hooks/usePermissions";
import Loader from "../widgets/Loader";

interface RequirePermissionProps {
  /** Single required permission. */
  permission?: string;
  /** OR — at least one of these. */
  anyOf?: string[];
  /** AND — all of these. */
  allOf?: string[];
  /**
   * Where to send the user when they don't have permission.
   * Defaults to "/no-access" (full-page 403, rendered outside BaseLayout).
   */
  redirectTo?: string;
  children: ReactNode;
}

/**
 * Route-level permission gate.
 *   - While `/user-info` is still loading: shows a centered Loader.
 *   - When the user has the permission: renders `children`.
 *   - When the user lacks the permission: navigates to `/no-access` (or the
 *     caller-supplied `redirectTo`), passing the original path + the missing
 *     permission keys as router state so the 403 page can show them.
 *
 * The 403 page renders *outside* BaseLayout so the sidebar / top bar don't
 * frame an error — it's a real take-over screen.
 *
 *   <Route path="/users" element={
 *     <RequirePermission permission="users:view">
 *       <Users />
 *     </RequirePermission>
 *   } />
 */
export default function RequirePermission({
  permission,
  anyOf,
  allOf,
  redirectTo = "/no-access",
  children,
}: RequirePermissionProps) {
  const { can, canAny, canAll, isReady } = usePermissions();
  const location = useLocation();

  const token = localStorage.getItem("auth_token");
  if (!token) {
    return <Navigate to="/session-expired" replace />;
  }

  if (!isReady) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  const allowed = permission
    ? can(permission)
    : anyOf
      ? canAny(anyOf)
      : allOf
        ? canAll(allOf)
        : true;

  if (!allowed) {
    return (
      <Navigate
        to={redirectTo}
        replace
        state={{
          from: location.pathname + location.search,
          requiredPermission: permission,
          requiredAnyOf: anyOf || allOf,
        }}
      />
    );
  }

  return <>{children}</>;
}
